import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  RefreshIcon,
  Select,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { buildCreateWorkOrderPath } from './buildCreateWorkOrderPath';
import type { DueBoardResponse, DueJobRow, DueOpenWorkOrder } from './dueJobTypes';

type BoardTab = 'pending-counter' | 'overdue';
type StatusFilter = 'all' | 'WARNING' | 'DUE' | 'OVERDUE';
type WorkOrderFilter = 'all' | 'open' | 'none';

const statusVariant: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  OVERDUE: 'danger',
  DUE: 'warning',
  WARNING: 'warning',
  PLANNED: 'success',
  CONDITION: 'info',
  UNKNOWN: 'default',
};

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'pending-counter',
    label: 'Pending counters',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'No pending counter jobs',
    emptyHint: 'No counter jobs are due or due soon.',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    icon: <CalendarIcon className="h-4 w-4" />,
    emptyTitle: 'No overdue jobs',
    emptyHint: 'No overdue calendar jobs.',
  },
];

const dueStatusLabel = (status?: string | null) => {
  const s = (status ?? '').toUpperCase();
  if (!s) return null;
  if (s === 'WARNING') return 'Due soon';
  if (s === 'PLANNED') return 'OK';
  if (s === 'CONDITION') return 'Observation';
  return s.charAt(0) + s.slice(1).toLowerCase();
};

const formatNextDue = (job: DueJobRow) => {
  const s = job.primarySchedule;
  if (!s) return '';
  if (s.nextDueDate) return String(s.nextDueDate).slice(0, 10);
  if (s.nextDueCounter != null) {
    const unit = s.counter?.unit ? ` ${s.counter.unit}` : '';
    return `${s.nextDueCounter}${unit}`;
  }
  return '';
};

const vesselName = (job: DueJobRow) => {
  const v = job.equipment?.vessel;
  return v?.vesselName ?? v?.name ?? (job.equipment?.vesselId != null ? `#${job.equipment.vesselId}` : '');
};

const equipmentLabel = (job: DueJobRow) => {
  const eq = job.equipment;
  if (!eq) return '';
  const code = eq.equipmentCode ?? eq.code;
  if (code && eq.name) return `${code} · ${eq.name}`;
  return eq.name ?? code ?? (eq.id != null ? `#${eq.id}` : '');
};

const sortValue = (row: DueJobRow, key: string): string | number => {
  switch (key) {
    case 'jobCode':
      return (row.jobCode || '').toLowerCase();
    case 'vessel':
      return vesselName(row).toLowerCase();
    case 'equipment':
      return equipmentLabel(row).toLowerCase();
    case 'nextDue': {
      const s = row.primarySchedule;
      if (s?.nextDueDate) return String(s.nextDueDate).slice(0, 10);
      if (s?.nextDueCounter != null) return Number(s.nextDueCounter);
      return '';
    }
    case 'status':
      return (row.dueStatus || '').toUpperCase();
    case 'workOrder':
      return (row.openWorkOrder?.workOrderNumber || '').toLowerCase();
    default:
      return '';
  }
};

const OpenWorkOrderCell: React.FC<{
  wo: DueOpenWorkOrder | null | undefined;
  extraCount?: number;
  textMuted: string;
}> = ({ wo, extraCount = 0, textMuted }) => {
  if (!wo) {
    return <span className={cn('text-sm', textMuted)}></span>;
  }
  const statusLabel = wo.status?.name ?? wo.status?.code ?? null;
  return (
    <span className="inline-flex min-w-0 flex-col gap-0.5">
      <Link
        to={`/pms/pms-work-orders/${wo.id}/edit`}
        state={{ from: '/pms/due-jobs' }}
        onClick={(e) => e.stopPropagation()}
        className="truncate font-medium text-sky-700 hover:underline dark:text-sky-300"
      >
        {wo.workOrderNumber || `WO #${wo.id}`}
      </Link>
      <span className={cn('truncate text-xs', textMuted)}>
        {statusLabel ?? 'Open'}
        {extraCount > 0 ? ` · +${extraCount} more` : ''}
      </span>
    </span>
  );
};

export const PmsDueJobsPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };

  const [tab, setTab] = useState<BoardTab>('pending-counter');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [vesselId, setVesselId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [woFilter, setWoFilter] = useState<WorkOrderFilter>('all');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });

  const vesselOptions = useMemo(() => {
    const list = Array.isArray(vesselsRaw) ? vesselsRaw : vesselsRaw?.data ?? [];
    return list
      .filter((v) => v.isActive !== false)
      .map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const statusOptions = useMemo(() => {
    if (tab === 'overdue') {
      return [{ value: 'OVERDUE', label: 'Overdue only' }];
    }
    return [
      { value: 'WARNING', label: 'Due soon' },
      { value: 'DUE', label: 'Due' },
    ];
  }, [tab]);

  const woOptions = [
    { value: 'open', label: 'Has open WO' },
    { value: 'none', label: 'Needs WO' },
  ];

  const boardUrl = useMemo(() => {
    const params = new URLSearchParams({ view: 'all' });
    if (vesselId) params.set('vesselId', vesselId);
    return `/pms-maintenance-jobs/due-board?${params.toString()}`;
  }, [vesselId]);

  const { data, error, isLoading, mutate, isValidating } = useSWR<DueBoardResponse>(boardUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort({ key: 'status', direction: 'asc' });

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, statusFilter, woFilter, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const pendingCount = data?.counts.pendingCounter ?? 0;
  const overdueCount = data?.counts.overdue ?? 0;

  const baseRows = useMemo(
    () => (tab === 'pending-counter' ? data?.pendingCounterJobs ?? [] : data?.overdueJobs ?? []),
    [tab, data?.pendingCounterJobs, data?.overdueJobs],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseRows.filter((row) => {
      if (statusFilter !== 'all') {
        if ((row.dueStatus ?? '').toUpperCase() !== statusFilter) return false;
      }
      if (woFilter === 'open' && !row.openWorkOrder) return false;
      if (woFilter === 'none' && row.openWorkOrder) return false;
      if (!q) return true;
      const hay = [
        row.jobCode,
        row.title,
        row.description,
        equipmentLabel(row),
        vesselName(row),
        row.responsibleRank?.name,
        row.responsibleRank?.code,
        row.openWorkOrder?.workOrderNumber,
        row.openWorkOrder?.title,
        row.primarySchedule?.counter?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [baseRows, search, statusFilter, woFilter]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [filteredRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim() || vesselId || statusFilter !== 'all' || woFilter !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setStatusFilter('all');
    setWoFilter('all');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
    setStatusFilter('all');
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);
  const tabCounts: Record<BoardTab, number> = {
    'pending-counter': pendingCount,
    overdue: overdueCount,
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search jobs…',
        title: 'Searches job, equipment, vessel, WO, rank',
      }}
      actions={
        <button
          type="button"
          onClick={() => {
            void mutate();
          }}
          disabled={isValidating}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium shadow-sm',
            colors.border,
            colors.text,
            isValidating && 'opacity-60',
          )}
          title="Refresh"
        >
          <RefreshIcon className={cn('h-4 w-4', isValidating && 'animate-spin')} />
          Refresh
        </button>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={statusFilter === 'all' ? '' : statusFilter}
              options={statusOptions}
              placeholder="Status"
              showClearButton
              onChange={(e) => setStatusFilter((e.target.value || 'all') as StatusFilter)}
            />
          </div>
          <div className="w-[150px]">
            <Select
              value={woFilter === 'all' ? '' : woFilter}
              options={woOptions}
              placeholder="Work order"
              showClearButton
              onChange={(e) => setWoFilter((e.target.value || 'all') as WorkOrderFilter)}
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading due jobs…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'pending-counter' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching jobs' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : undefined,
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load due jobs.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn(
                'sticky left-0 z-20 whitespace-nowrap px-3 py-3 font-medium',
                colors.surface,
              )}
              style={{ minWidth: '5.5rem' }}
            />
            <GeneralListSortableTh sortKey="jobCode" className="whitespace-nowrap" style={{ minWidth: '14rem' }}>
              Job
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="equipment" className="whitespace-nowrap" style={{ minWidth: '12rem' }}>
              Equipment
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="nextDue" className="whitespace-nowrap" style={{ minWidth: '8.5rem' }}>
              {tab === 'pending-counter' ? 'Target' : 'Due date'}
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="whitespace-nowrap" style={{ minWidth: '7rem' }}>
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="workOrder" className="whitespace-nowrap" style={{ minWidth: '10rem' }}>
              Work order
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const openWo = row.openWorkOrder;
            const createPath = !openWo ? buildCreateWorkOrderPath(row) : null;
            const counter = row.primarySchedule?.counter;
            const statusKey = (row.dueStatus ?? 'UNKNOWN').toUpperCase();
            const statusText = dueStatusLabel(row.dueStatus);
            const dueLabel = formatNextDue(row);
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/pms/pms-maintenance-jobs/${row.id}`, { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td
                  className={cn(
                    'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                    colors.surface,
                    'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-nowrap items-center">
                    {openWo ? (
                      <Link
                        to={`/pms/pms-work-orders/${openWo.id}/edit`}
                        state={returnState}
                        title="Edit work order"
                        aria-label="Edit work order"
                        className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    ) : createPath ? (
                      <Link
                        to={createPath}
                        state={returnState}
                        title="Create work order"
                        aria-label="Create work order"
                        className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                      >
                        <DocumentTextIcon className="h-5 w-5" />
                      </Link>
                    ) : null}
                    <Link
                      to={`/pms/pms-maintenance-jobs/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="Open job"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                  </div>
                </td>
                <td className="max-w-[18rem] px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{row.jobCode || `#${row.id}`}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>{row.title || ''}</p>
                  </div>
                </td>
                <td
                  className={cn('max-w-[12rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={vesselName(row)}
                >
                  {vesselName(row)}
                </td>
                <td
                  className={cn('max-w-[14rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={equipmentLabel(row)}
                >
                  {equipmentLabel(row)}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <span className="inline-flex min-w-0 flex-col gap-0.5">
                    <span className={cn('truncate', colors.textMuted)}>{dueLabel}</span>
                    {tab === 'pending-counter' && counter?.name ? (
                      <span className={cn('truncate text-xs', colors.textMuted)}>{counter.name}</span>
                    ) : null}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  {statusText ? (
                    <Badge variant={statusVariant[statusKey] ?? 'default'} className="whitespace-nowrap">
                      {statusText}
                    </Badge>
                  ) : (
                    <span className="text-slate-400"></span>
                  )}
                </td>
                <td className="max-w-[12rem] px-3 py-3.5">
                  <OpenWorkOrderCell
                    wo={row.openWorkOrder}
                    extraCount={Math.max(0, (row.openWorkOrders?.length ?? 0) - (row.openWorkOrder ? 1 : 0))}
                    textMuted={colors.textMuted}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default PmsDueJobsPage;
